import React, { useState, useContext, useEffect } from "react";
import { AuthContext } from "../store/AuthContext";
import { adminLogin, teacherLogin, studentLogin } from "../api/authApi";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  TextField,
  Button,
  Typography,
  MenuItem,
  Container,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
  Paper
} from "@mui/material";
import {
  Lock,
  Phone,
  Person,
  School,
  AdminPanelSettings,
  Visibility,
  VisibilityOff,
  Login as LoginIcon
} from "@mui/icons-material";

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, user, role } = useContext(AuthContext);

  const [selectedRole, setSelectedRole] = useState("student");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // 🔥 AUTO-REDIRECT IF USER ALREADY LOGGED IN
  useEffect(() => {
    if (user && role) {
      navigate(`/${role}`);
    }
  }, [user, role, navigate]);

  const handleLogin = async () => {
    if (!phone.trim() || !password.trim()) {
      setError("Please enter phone number and password");
      return;
    }

    setLoading(true);
    setError("");

    try {
      let res;
      if (selectedRole === "admin") res = await adminLogin(phone, password);
      if (selectedRole === "teacher") res = await teacherLogin(phone, password);
      if (selectedRole === "student") res = await studentLogin(phone, password);

      if (!res.data.success) {
        setError(res.data.message || "Invalid credentials");
        return;
      }

      login(res.data.token, res.data.user);
      navigate(`/${res.data.user.role}`);

    } catch (error) {
      console.error(error);
      setError("Login failed. Please check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleLogin();
    }
  };

  const getRoleColor = () => {
    switch(selectedRole) {
      case 'admin': return '#d32f2f';
      case 'teacher': return '#1976d2';
      case 'student': return '#388e3c';
      default: return '#1976d2';
    }
  };

  const getRoleIcon = () => {
    switch(selectedRole) {
      case 'admin': return <AdminPanelSettings />;
      case 'teacher': return <School />;
      case 'student': return <Person />;
      default: return <Person />;
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 4,
        }}
      >
        <Card
          elevation={6}
          sx={{
            width: '100%',
            maxWidth: 450,
            p: { xs: 3, sm: 4 },
            borderRadius: 3,
            borderTop: `4px solid ${getRoleColor()}`,
          }}
        >
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: '50%',
                bgcolor: `${getRoleColor()}20`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <Lock sx={{ fontSize: 30, color: getRoleColor() }} />
            </Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Welcome Back
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Sign in to your {selectedRole} account
            </Typography>
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert 
              severity="error" 
              sx={{ mb: 3, borderRadius: 2 }}
              onClose={() => setError("")}
            >
              {error}
            </Alert>
          )}

          {/* Role Selection */}
          <TextField
            select
            fullWidth
            label="Select Role"
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value)}
            sx={{ mb: 3 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {getRoleIcon()}
                </InputAdornment>
              ),
            }}
          >
            <MenuItem value="admin">Admin</MenuItem>
            <MenuItem value="teacher">Teacher</MenuItem>
            <MenuItem value="student">Student</MenuItem>
          </TextField>

          {/* Phone Input */}
          <TextField
            fullWidth
            label="Phone Number"
            placeholder="Enter your phone number"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            onKeyPress={handleKeyPress}
            sx={{ mb: 3 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Phone />
                </InputAdornment>
              ),
            }}
          />

          {/* Password Input */}
          <TextField
            fullWidth
            label="Password"
            type={showPassword ? 'text' : 'password'}
            placeholder="Enter your password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyPress={handleKeyPress}
            sx={{ mb: 4 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Login Button */}
          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={handleLogin}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <LoginIcon />}
            sx={{
              py: 1.5,
              borderRadius: 2,
              bgcolor: getRoleColor(),
              fontWeight: 'bold',
              fontSize: '1rem',
              '&:hover': {
                bgcolor: getRoleColor(),
                transform: 'translateY(-1px)',
                boxShadow: 4,
              },
              transition: 'all 0.2s ease',
            }}
          >
            {loading ? 'Signing In...' : 'Sign In'}
          </Button>

          {/* Footer Note */}
          <Typography
            variant="body2"
            color="text.secondary"
            align="center"
            sx={{ mt: 4 }}
          >
            © 2024 Learning Management System
          </Typography>
        </Card>

        {/* Background Decoration */}
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `linear-gradient(135deg, ${getRoleColor()}10 0%, #ffffff 100%)`,
            zIndex: -1,
          }}
        />
      </Box>
    </Container>
  );
}